import type {
  IChipInstanceModel,
  IConnectionModel,
  ITileManagerModel,
} from '@/interfaces/models';
import { ConnectionGraph } from '@/models/connections/ConnectionGraph';
import { analyzeBundleCompatibility } from './ProspectiveContainment';

export const BUNDLE_COMPATIBILITY_MESSAGE =
  'Bundled interfaces require instances of the same chip definition. Disconnect this path or use the same chip; saved wiring is preserved.';

export const BUNDLE_COMPATIBILITY_BUDGET_MESSAGE =
  'Bundle compatibility exceeds the topology limit. Separate this network to validate it; saved wiring is preserved.';

export function compatibleBundleConnections(
  manager: ITileManagerModel,
  connections: IConnectionModel,
  instances: readonly IChipInstanceModel[],
) {
  const edges = connections.dto.connections;
  const result = analyzeBundleCompatibility(manager, edges, instances);
  const blocked = new Set(result.blocked);
  const graph = new ConnectionGraph({
    connections: edges.filter((edge) => !blocked.has(edge)),
  });
  // A runtime view only. The authored DTO, undo history and manual edges stay intact.
  const view = new Proxy({} as IConnectionModel, {
    get(_target, property) {
      if (property === 'connectionFlags')
        return (id: Parameters<IConnectionModel['connectionFlags']>[0]) =>
          graph.getConnectionFlags(id);
      if (property === 'connectedNeighbors')
        return (id: Parameters<IConnectionModel['connectedNeighbors']>[0]) =>
          graph.getConnectedNeighbors(id);
      if (property === 'connectionsFor')
        return (id: Parameters<IConnectionModel['connectionsFor']>[0]) =>
          new Map(
            graph
              .getConnectedNeighbors(id)
              .map((neighbor) => [neighbor.viaSide, neighbor.point]),
          );
      return Reflect.get(connections, property, connections);
    },
  });
  return { ...result, view, graph };
}
